/**
 * RestaurantPage — URL 직접 진입 시 매장 메뉴 페이지
 *
 * QRVerifyPage 가 navigate 하는 두 경로를 처리:
 *   /restaurant/:id            — 픽업 주문 (QR pickup 흐름)
 *   /restaurant/:id/table/:tableNum — 테이블 주문 (QR table 흐름)
 *
 * @see src/config/routes.ts ROUTES.RESTAURANT / ROUTES.RESTAURANT_TABLE
 * @see Issue #431 — Route 미등록으로 인한 404 버그 수정
 */

import { useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PickupMenu, type PickupWaitlistGate } from '@/components/PickupMenu';
import { storeApi } from '@/lib/api/stores';
import { ApiClientError } from '@/lib/api/client';
import { getQrSessionTranslation } from '@/i18n/qrSession';
import { waitlistApi } from '@/lib/api/waitlist';
import { readWaitlistToken } from '@/components/waitlist/storage';
import { useCart } from '@/contexts/CartContext';
import { useApp } from '@/contexts/AppContext';
import { isViewOnlyMode, getWaitlistPath } from '@/lib/app-utils';
import {
  getValidatedTableSessionEntryForRoute,
  getValidatedTableSessionSnapshotForRoute,
  restoreTableSessionFromRecord,
} from '@/lib/tableContext';
import {
  TABLE_SESSION_CHANGED_EVENT,
  TABLE_SESSION_ENTRY_KEY,
} from '@/lib/sessionStorageKeys';
import { getRestaurantPath } from '@/config/routes';
import { createInitialEntrySessionTerminal, type SessionTerminalContext } from '@/lib/sessionTerminal';
import { useSessionTermination } from '@/hooks/useSessionTermination';
import type { StoreDetail } from '@/lib/api/types';
import type { CartItem } from '@/components/CartModal';
import { logger } from '@/utils/logger';
import { safeErrorMetadata } from '@/utils/safeErrorMetadata';
import type { Language, Locale } from '@/config/locales';

/** 페이지 문자열 타입 */
interface RestaurantPageStrings {
  storeFetchFailed: string;
  storeNotFound: string;
  goHome: string;
  /**
   * 픽업 줄서기 게이트(admin#2172/menu#804) — 게이트 매장에서 called 미확인 시
   * 장바구니/결제 CTA 대신 노출하는 배너 문구.
   */
  waitlistGateTitle: string;
  waitlistGateDescription: string;
  waitlistGateCta: string;
}

/**
 * 매장 조회 전용 문자열. QR/세션 문자열은 src/i18n/qrSession.ts 단일 출처를 사용한다.
 * #522 — Locale 단일 출처 정합 (15언어 전수, 키 누락 시 컴파일 에러).
 */
const RESTAURANT_PAGE_TRANSLATIONS:
  & Record<Language, RestaurantPageStrings>
  & Record<Exclude<Locale, Language>, RestaurantPageStrings> = {
  ko: {
    storeFetchFailed: '매장 정보를 불러오지 못했습니다.',
    storeNotFound: '매장을 찾을 수 없습니다.',
    goHome: '홈으로 돌아가기',
    waitlistGateTitle: '줄서기 후 주문 가능한 매장입니다',
    waitlistGateDescription: '줄서기에서 호출을 받은 뒤 주문할 수 있어요. 메뉴는 자유롭게 둘러보실 수 있습니다.',
    waitlistGateCta: '줄서기 하러 가기',
  },
  es: {
    storeFetchFailed: 'No se pudo cargar la información de la tienda.',
    storeNotFound: 'Tienda no encontrada.',
    goHome: 'Volver al inicio',
    waitlistGateTitle: 'Este local requiere hacer fila para ordenar',
    waitlistGateDescription: 'Podrás ordenar cuando te llamen en la fila. Mientras tanto, puedes ver el menú libremente.',
    waitlistGateCta: 'Ir a la fila',
  },
  en: {
    storeFetchFailed: 'Could not load store information.',
    storeNotFound: 'Store not found.',
    goHome: 'Go home',
    waitlistGateTitle: 'Join the line to order here',
    waitlistGateDescription: 'You\'ll be able to order once you\'re called in the line. Feel free to browse the menu.',
    waitlistGateCta: 'Join the line',
  },
  pt: {
    storeFetchFailed: 'Não foi possível carregar as informações da loja.',
    storeNotFound: 'Loja não encontrada.',
    goHome: 'Voltar ao início',
    waitlistGateTitle: 'Este local exige fila para pedir',
    waitlistGateDescription: 'Você poderá pedir assim que for chamado na fila. Enquanto isso, sinta-se à vontade para ver o cardápio.',
    waitlistGateCta: 'Entrar na fila',
  },
  fr: {
    storeFetchFailed: 'Impossible de charger les informations du magasin.',
    storeNotFound: 'Magasin introuvable.',
    goHome: "Retour à l'accueil",
    waitlistGateTitle: 'Ce restaurant nécessite de faire la file pour commander',
    waitlistGateDescription: 'Vous pourrez commander une fois appelé dans la file. Vous pouvez consulter le menu librement en attendant.',
    waitlistGateCta: 'Rejoindre la file',
  },
  zh: {
    storeFetchFailed: '无法加载门店信息。',
    storeNotFound: '未找到该门店。',
    goHome: '返回首页',
    waitlistGateTitle: '此门店需要排队后才能下单',
    waitlistGateDescription: '轮到您时即可下单。在此之前您可以自由浏览菜单。',
    waitlistGateCta: '去排队',
  },
  ja: {
    storeFetchFailed: '店舗情報を読み込めませんでした。',
    storeNotFound: '店舗が見つかりません。',
    goHome: 'ホームに戻る',
    waitlistGateTitle: 'この店舗は順番待ちをしてから注文できます',
    waitlistGateDescription: '順番が呼ばれると注文できます。それまではメニューを自由にご覧いただけます。',
    waitlistGateCta: '順番待ちに参加する',
  },
  hi: {
    storeFetchFailed: 'स्टोर की जानकारी लोड नहीं हो सकी।',
    storeNotFound: 'स्टोर नहीं मिला।',
    goHome: 'होम पर वापस जाएं',
    waitlistGateTitle: 'इस स्टोर पर ऑर्डर करने के लिए कतार में लगना होगा',
    waitlistGateDescription: 'कतार में आपकी बारी आने पर आप ऑर्डर कर सकेंगे। तब तक आप मेन्यू स्वतंत्र रूप से देख सकते हैं।',
    waitlistGateCta: 'कतार में शामिल हों',
  },
  de: {
    storeFetchFailed: 'Geschäftsinformationen konnten nicht geladen werden.',
    storeNotFound: 'Geschäft nicht gefunden.',
    goHome: 'Zur Startseite',
    waitlistGateTitle: 'Dieses Geschäft erfordert Anstehen vor der Bestellung',
    waitlistGateDescription: 'Sobald Sie aufgerufen werden, können Sie bestellen. In der Zwischenzeit können Sie die Speisekarte frei durchsehen.',
    waitlistGateCta: 'Warteschlange beitreten',
  },
  it: {
    storeFetchFailed: 'Impossibile caricare le informazioni del negozio.',
    storeNotFound: 'Negozio non trovato.',
    goHome: 'Torna alla home',
    waitlistGateTitle: 'Questo locale richiede la fila per ordinare',
    waitlistGateDescription: 'Potrai ordinare quando sarai chiamato in fila. Nel frattempo puoi sfogliare liberamente il menu.',
    waitlistGateCta: 'Unisciti alla fila',
  },
  ru: {
    storeFetchFailed: 'Не удалось загрузить информацию о магазине.',
    storeNotFound: 'Магазин не найден.',
    goHome: 'На главную',
    waitlistGateTitle: 'В этом заведении нужно встать в очередь перед заказом',
    waitlistGateDescription: 'Вы сможете сделать заказ, когда вас вызовут в очереди. А пока вы можете свободно просматривать меню.',
    waitlistGateCta: 'Встать в очередь',
  },
  ar: {
    storeFetchFailed: 'تعذر تحميل معلومات المتجر.',
    storeNotFound: 'المتجر غير موجود.',
    goHome: 'العودة إلى الصفحة الرئيسية',
    waitlistGateTitle: 'يتطلب هذا المتجر الانتظار في الطابور قبل الطلب',
    waitlistGateDescription: 'يمكنك الطلب بمجرد استدعائك في الطابور. في هذه الأثناء يمكنك تصفح القائمة بحرية.',
    waitlistGateCta: 'انضم إلى الطابور',
  },
  vi: {
    storeFetchFailed: 'Không thể tải thông tin cửa hàng.',
    storeNotFound: 'Không tìm thấy cửa hàng.',
    goHome: 'Về trang chủ',
    waitlistGateTitle: 'Cửa hàng này yêu cầu xếp hàng trước khi đặt món',
    waitlistGateDescription: 'Bạn có thể đặt món khi được gọi trong hàng đợi. Trong lúc chờ, bạn có thể xem thực đơn thoải mái.',
    waitlistGateCta: 'Tham gia hàng đợi',
  },
  th: {
    storeFetchFailed: 'ไม่สามารถโหลดข้อมูลร้านค้าได้',
    storeNotFound: 'ไม่พบร้านค้า',
    goHome: 'กลับสู่หน้าหลัก',
    waitlistGateTitle: 'ร้านนี้ต้องต่อคิวก่อนสั่งอาหาร',
    waitlistGateDescription: 'คุณจะสั่งอาหารได้เมื่อถึงคิวของคุณ ระหว่างนี้สามารถดูเมนูได้อย่างอิสระ',
    waitlistGateCta: 'ไปต่อคิว',
  },
  nl: {
    storeFetchFailed: 'Kon de winkelinformatie niet laden.',
    storeNotFound: 'Winkel niet gevonden.',
    goHome: 'Naar home',
    waitlistGateTitle: 'Deze winkel vereist wachtrij voor bestellen',
    waitlistGateDescription: 'Zodra u wordt opgeroepen in de rij, kunt u bestellen. Ondertussen kunt u vrij door het menu bladeren.',
    waitlistGateCta: 'Sluit aan in de rij',
  },
};

/** 로딩/에러/성공 3가지 상태 */
type PageState =
  | { status: 'loading' }
  | { status: 'error'; message: string; retryable?: boolean }
  | ({
      status: 'success';
      store: StoreDetail;
      routeKey: string;
    } & (
      | { mode: 'table'; sessionToken: string | null; entryToken?: string }
      | { mode: 'pickup' | 'view'; sessionToken?: never }
    ));

export interface RestaurantPageProps {
  orderedItemCount?: number;
  onPaymentClick?: () => void;
}

export function RestaurantPage({
  orderedItemCount,
  onPaymentClick,
}: RestaurantPageProps = {}) {
  const { id, tableNum } = useParams<{ id: string; tableNum?: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  // #522: 15 locale 전수 — language(3종) 대신 locale 로 직접 조회
  const { locale } = useApp();

  // #541: ?mode=view 쿼리 파라미터 → 익명 순수 메뉴판 모드.
  // App.tsx 와 동일한 isViewOnlyMode 헬퍼로 view 판정 단일 출처 유지 (드리프트 예방).
  const isViewOnly = isViewOnlyMode(location.pathname, searchParams.get('mode'));

  /** es 기본 fallback (멕시코 서비스) */
  const t = useMemo(
    () => RESTAURANT_PAGE_TRANSLATIONS[locale] ?? RESTAURANT_PAGE_TRANSLATIONS.es,
    [locale],
  );
  const [state, setState] = useState<PageState>({ status: 'loading' });
  const [readAttempt, setReadAttempt] = useState(0);
  const retryPending = useRef(false);
  const [initialEntryTerminal, setInitialEntryTerminal] =
    useState<SessionTerminalContext | null>(null);
  useSessionTermination(initialEntryTerminal);
  const [tableSessionVersion, setTableSessionVersion] = useState(0);

  useEffect(() => {
    const handleSessionChange = (): void => {
      if (!tableNum) return;
      try {
        const hasIncomingTableContext = window.sessionStorage.getItem('table_session_token') !== null
          || window.sessionStorage.getItem(TABLE_SESSION_ENTRY_KEY) !== null;
        // 이 구독은 entry 생성/entry→joined 승격처럼 새 컨텍스트가 들어오는 전환용이다.
        // completed cleanup의 제거 이벤트까지 재검증하면 이미 확정한 completed terminal을
        // 새 initial_entry invalid가 덮는다. 제거는 기존 terminal/outer route 경계가 담당한다.
        if (hasIncomingTableContext) {
          setTableSessionVersion((version) => version + 1);
        }
      } catch {
        // storage 접근 실패는 기존 렌더의 fail-closed 판정을 유지한다.
      }
    };
    window.addEventListener(TABLE_SESSION_CHANGED_EVENT, handleSessionChange);
    return () => { window.removeEventListener(TABLE_SESSION_CHANGED_EVENT, handleSessionChange); };
  }, [tableNum]);

  const routeKey = `${id ?? ''}:${tableNum ?? ''}`;

  useEffect(() => {
    // React Router `:id` 매칭이 id 존재를 보장하므로 dead branch 불가.
    // dev 안전망: id 없을 경우 throw (dev-only, 운영 도달 불가)
    if (!id) throw new Error('[RestaurantPage] id 없음 — Route 미등록 의심');

    // P0/P1: 테이블 URL 이지만 세션 토큰 없는 경우 — fetch 전 조기 차단 (#431)
    // #670: 차단 전에 localStorage 복구 record 복원/legacy 3키 승격 시도.
    // runtime token이 있으면 같은 token/table의 미만료 record만 trusted storeId를
    // 보강하고, token이 없으면 route store/table이 일치하는 record 단독 복원을 허용한다.
    // (불일치/만료/record 부재 시 기존 "세션 만료" 동선 유지 — fail-safe.
    //  복원 토큰의 서버 권위 검증은 useSessionTimer 가 수행: 401/410/404 → clearTableSession)
    let validatedSessionToken: string | null = null;
    let validatedEntryToken: string | null = null;
    if (tableNum) {
      let snapshot = getValidatedTableSessionSnapshotForRoute(location.pathname);
      if (!snapshot) {
        restoreTableSessionFromRecord({
          storeId: Number(id),
          tableNumber: tableNum,
        });
        snapshot = getValidatedTableSessionSnapshotForRoute(location.pathname);
      }

      if (!snapshot) {
        validatedEntryToken = getValidatedTableSessionEntryForRoute(location.pathname)?.entryToken
          ?? null;
        if (validatedEntryToken === null) {
          setState({ status: 'loading' });
          setInitialEntryTerminal(createInitialEntrySessionTerminal());
          return;
        }
      } else {
        validatedSessionToken = snapshot.token;
      }
    }

    const controller = new AbortController();
    setState({ status: 'loading' });
    setInitialEntryTerminal(null);

    (async () => {
      try {
        const response = await storeApi.getStore(id, controller.signal);
        if (controller.signal.aborted) return;

        // Claude P0-4: fetch 응답 가드
        if (!response?.data) {
          setState({ status: 'error', message: t.storeNotFound });
          return;
        }

        // #610: 직접 /restaurant/:id 진입은 픽업 주문 의도가 확정된 QR이 아니다.
        // 픽업 비활성 매장은 메뉴 열람 전용 view mode 로 전환하고, pickup QR 차단은 QRVerifyPage 가 유지한다.
        if (!tableNum && !isViewOnly && !response.data.pickup_available) {
          navigate(`${getRestaurantPath(id)}?mode=view`, { replace: true });
          return;
        }

        const mode: 'table' | 'pickup' | 'view' = tableNum
          ? 'table'
          : isViewOnly
            ? 'view'
            : 'pickup';
        if (mode === 'table') {
          setState({
            status: 'success',
            store: response.data,
            routeKey,
            mode,
            sessionToken: validatedSessionToken,
            ...(validatedEntryToken === null ? {} : { entryToken: validatedEntryToken }),
          });
        } else {
          setState({
            status: 'success',
            store: response.data,
            routeKey,
            mode,
          });
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        retryPending.current = false;
        const message =
          error instanceof Error ? error.message : t.storeFetchFailed;
        logger.error('RestaurantPage: store fetch failed:', error, {
          error: safeErrorMetadata(error),
        });
        const retryable = error instanceof ApiClientError && (
          (error.status === 408 && error.code === 'TIMEOUT') ||
          (error.status === 0 && error.code === 'NETWORK_ERROR')
        );
        setState({ status: 'error', message, retryable });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [id, tableNum, routeKey, t, isViewOnly, location.pathname, navigate, tableSessionVersion, readAttempt]);

  /**
   * 픽업 줄서기 게이트(admin#2172/menu#804) — `store.pickup_requires_waitlist=true`
   * 매장은 보관 토큰의 파티가 픽업 큐 + called 상태일 때만 주문(장바구니/결제 진입)을
   * 허용한다. 그 외(토큰 없음/타 상태/조회 실패)는 fail-closed 로 차단 — RestaurantPage
   * 는 메뉴 열람은 그대로 두고 PickupMenu 에 차단 상태만 전달한다.
   *
   * table/view 모드는 이 게이트 대상이 아니다(픽업 주문 게이트이므로).
   */
  const [waitlistGateAllowed, setWaitlistGateAllowed] = useState(false);

  useEffect(() => {
    if (state.status !== 'success') {
      return;
    }
    const { store } = state;

    if (state.mode === 'table' || state.mode === 'view' || !store.pickup_requires_waitlist) {
      setWaitlistGateAllowed(true);
      return;
    }

    const token = readWaitlistToken(store.id);
    if (!token) {
      setWaitlistGateAllowed(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    setWaitlistGateAllowed(false);

    waitlistApi
      .getParty(token, controller.signal)
      .then((response) => {
        if (cancelled) return;
        const party = response.data;
        setWaitlistGateAllowed(party.queue_type === 'pickup' && party.status === 'called');
      })
      .catch((error) => {
        if (cancelled || (error instanceof DOMException && error.name === 'AbortError')) return;
        // 조회 실패(토큰 만료/네트워크 등) — fail-closed 로 차단 유지.
        logger.warn('[waitlist] 픽업 게이트 파티 조회 실패', error);
        setWaitlistGateAllowed(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [state]);

  if (state.status === 'success' && state.routeKey !== routeKey) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  // ----- 로딩 -----
  if (state.status === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
      </div>
    );
  }

  // ----- 에러 -----
  if (state.status === 'error') {
    return (
      <div className="flex flex-col items-center justify-center min-h-[70vh] gap-4 px-4">
        <AlertCircle className="h-10 w-10 text-destructive" />
        <p className="text-center text-foreground">{state.message}</p>
        {state.retryable && (
          <Button onClick={() => {
            // A user-initiated read gets a fresh deadline. Never retry writes or
            // extend the automatic recovery budget, and coalesce rapid clicks.
            if (retryPending.current) return;
            retryPending.current = true;
            setState({ status: 'loading' });
            setReadAttempt((attempt) => attempt + 1);
          }}>
            {getQrSessionTranslation(locale).retry}
          </Button>
        )}
        <Button variant="outline" onClick={() => navigate('/', { replace: true })}>
          {t.goHome}
        </Button>
      </div>
    );
  }

  // ----- 성공 -----
  const { store, mode } = state;

  // CartItem 시그니처는 App.tsx 의 onAddToCart 와 동일 — CartContext.addToCart 직접 호출
  const handleAddToCart = (item: Omit<CartItem, 'quantity'>, quantity?: number) => {
    addToCart(item, quantity);
  };

  // 뒤로 가기: history가 있으면 -1, 없으면 홈
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/', { replace: true });
    }
  };

  const handleGoToWaitlist = () => {
    navigate(getWaitlistPath(store.slug));
  };

  // #804: 픽업 게이트 매장이고 아직 called 확인 전(또는 실패)이면 CTA 를 대체한다.
  // table/view 모드는 게이트 대상이 아니다.
  const pickupWaitlistGate: PickupWaitlistGate | undefined =
    mode === 'pickup' && store.pickup_requires_waitlist && !waitlistGateAllowed
      ? {
          title: t.waitlistGateTitle,
          description: t.waitlistGateDescription,
          ctaLabel: t.waitlistGateCta,
          onGoToWaitlist: handleGoToWaitlist,
        }
      : undefined;

  return (
    mode === 'table' ? (
      <PickupMenu
        restaurant={{
          id: String(store.id),
          nameKey: store.slug,
          name: store.name,
          cuisine: store.description ?? '',
          rating: store.rating,
          imageUrl: store.image_url ?? '',
        }}
        onBack={handleBack}
        onAddToCart={handleAddToCart}
        mode="table"
        sessionToken={state.sessionToken}
        entryToken={state.entryToken}
        orderedItemCount={orderedItemCount}
        onPaymentClick={onPaymentClick}
        pickupWaitlistGate={pickupWaitlistGate}
      />
    ) : (
      <PickupMenu
        restaurant={{
          id: String(store.id),
          nameKey: store.slug,
          name: store.name,
          cuisine: store.description ?? '',
          rating: store.rating,
          imageUrl: store.image_url ?? '',
        }}
        onBack={handleBack}
        onAddToCart={handleAddToCart}
        mode={mode}
        orderedItemCount={orderedItemCount}
        onPaymentClick={onPaymentClick}
        pickupWaitlistGate={pickupWaitlistGate}
      />
    )
  );
}
